import React, { useState, useEffect, useRef } from 'react';
import ReCAPTCHA from "react-google-recaptcha";
import FormControl from '@mui/material/FormControl';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LoadingButton from '@mui/lab/LoadingButton';
import SaveIcon from '@mui/icons-material/Save';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import ValidationFeedback from '../components/validation.feedback';
import ThankYouActivity from '../views/thankyou.activity';
import GetMemberForm from '../components/get-member.form';
import PlayerSelect from '../components/player.select';
import ActivityItem from './activity.item';
import { sortPlayers } from '../util/sort';

import GoogleSheetsService from '../service/googlesheet.service';

const ActivityForm = ({ reCAPTCHAActive, goBack, resetRegistrationType }) => {
    const [loading, setLoading] = useState(true);
    const [member, setMember] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [approveReCAPTCHA, setApproveReCAPTCHA] = useState(false);
    const [validationError, setValidationError] = useState('');
    const [showThankYou, setShowThankYou] = useState(0);
    const [activities, setActivities] = useState([]);
    const [participants, setParticipants] = useState([]);
    const [activitiesAvailable, setActivitiesAvailable] = useState([]);

    const recaptchaRef = useRef();

    useEffect(() => {
        if (showThankYou === 0) {
            setLoading(true);
            GoogleSheetsService.getActivities().then((result) => {
                setActivitiesAvailable(result ?? []);
                setLoading(false);
            });
        }
    }, [showThankYou]);

    const updateMember = (member) => {
        if (member) {
            setMember(member);
            member.players.map(p => {
                p.id = p.name;
                p.option = '';
                return p;
            });
            member.players = member.players.sort((a, b) => sortPlayers(a, b));
            setParticipants(member.players);
        } else {
            setParticipants([]);
            setMember({});
        }
    }

    const updateParticipants = (updatedParticipants) => {
        setParticipants(updatedParticipants);

        let updateActivities = [...activities];
        updateActivities.map(a => {
            a.participants = [...updatedParticipants.filter(p => p.participating === true)].map(p => {
                return { ...p, option: a.participants.find(ap => ap.id === p.id)?.option ?? '' }
            });
            return a;
        });

        for (const activity of activitiesAvailable) {
            if (activity.noOfOpenSpots < participants.filter(p => p.participating === true).length) {
                const fullActivityIndex = updateActivities.findIndex(a => a.id === activity.id);
                if (fullActivityIndex !== -1) {
                    updateActivities.splice(fullActivityIndex, 1);
                }
            }
        }

        setActivities(updateActivities);
    }

    const validateRegistration = () => {
        let activityOptionsSet = true;
        activities.map(a => {
            let activity = activitiesAvailable.filter(_ => _.id === a.id)[0];
            if (activity.options && activity.options.length > 0) {
                let participantOptionsUnset = a.participants.filter(_ => _.option === '' && participants.filter(p => p.id === _.id && p.name !== '').length > 0);
                if (participantOptionsUnset && participantOptionsUnset.length > 0) {
                    activityOptionsSet = false;
                }
            }
            return a;
        });
        if (participants.filter(p => p.participating === true).length === 0) {
            setValidationError('Du måste välja minst en deltagare');
        } else if (activities.length === 0) {
            setValidationError('Du måste välja minst en aktivitet');
        } else if ([...new Set(participants.filter(_ => _.name !== '').map(item => item.name))].length < participants.filter(_ => _.name !== '').length) {
            setValidationError('Du måste ange unika namn på alla deltagare');
        } else if (activityOptionsSet === false) {
            setValidationError('Du har missat att göra ett val för en aktivitet');
        } else {
            setValidationError('');

            //Set correct names
            activities.map(a => {
                a.activityId = a.id;
                a.participants.map(p => {
                    p.name = participants.filter(_ => _.id === p.id)[0].name;
                    p.phonenumber = member.phoneNumber;
                    return p;
                });
                return a;

            });

            setSubmitting(true);
            let recaptchaToken = '';
            if (reCAPTCHAActive === true) {
                recaptchaToken = recaptchaRef.current.getValue();
            }
            GoogleSheetsService.submitActivityRegistrations(member.email, activities, recaptchaToken).then((result) => {
                setSubmitting(false);
                setShowThankYou(result);
            });
        }
    }

    if (showThankYou > 0) {
        return (
            <React.Fragment>
                <ThankYouActivity email={member.email}
                    activities={[...activitiesAvailable]
                        .filter(aa => activities.filter(a => a.id === aa.id).length > 0)
                        .map(aa => {
                            let options = activities.find(a => a.id === aa.id).participants?.map(p => p.option).filter(o => o && o !== "");
                            aa.selection = options.length > 0 ? `(${options.join(", ")})` : "";
                            return aa;
                        })
                    }
                    participants={participants.filter(p => p.participating === true)}
                    newRegistration={() => {
                        setActivities([]);
                        setParticipants([]);
                        setActivitiesAvailable([]);
                        setMember({});
                        setShowThankYou(0);
                        setApproveReCAPTCHA(false);
                    }
                } />
            </React.Fragment>)
    } else {
        return (
            <div className="s4g-reg-form">
                {loading === true && <div className='s4g-section'>
                    {<h3>Hämtar aktiviteter, vänligen vänta lite...</h3>}
                </div>}

                {loading === false && <React.Fragment>
                    {goBack === true && <div className='s4g-section'>
                        <LoadingButton variant="outlined" startIcon={<ArrowBackIcon />} onClick={resetRegistrationType}>
                            Gå tillbaka
                        </LoadingButton>
                    </div>}
                    
                    <GetMemberForm updateMember={updateMember} required={true} label={"Kundnummer"} autoFocus={true} />

                    {participants.length > 0 && <div className='s4g-section'>
                        <h3>Vem vill du anmäla</h3>
                        <PlayerSelect players={participants} updatePlayers={updateParticipants} canAddGuest={true} />
                    </div>}

                    {member.players && <div className='s4g-section'>
                        <h3>Välj aktiviteter som ni vill anmäla er till</h3>
                        <p><i>Klicka på en aktivitet för mer information.</i></p>
                        {loading === false && activitiesAvailable.length > 0 && activitiesAvailable.map((activity) => {
                            return (<React.Fragment key={activity.id}>
                                <ActivityItem
                                    checked={activities.filter(_ => _.id === activity.id).length > 0}
                                    disabled={activity.noOfOpenSpots === 0 || activity.noOfOpenSpots < participants.filter(p => p.participating === true).length}
                                    onChange={e => {
                                        let updateActivities = [...activities];
                                        if (e.target.checked === true) {
                                            var addActivity = { id: activity.id, participants: [...participants.filter(p => p.participating === true)] };
                                            updateActivities.push(addActivity);
                                        } else {
                                            let activityIndex = updateActivities.findIndex(_ => _.id === activity.id);
                                            updateActivities.splice(activityIndex, 1);
                                        }
                                        setActivities(updateActivities);
                                    }}
                                    title={`${activity.dateDisplay} - ${activity.name}`}
                                    description={activity.description}
                                    noOfOpenSpots={activity.noOfOpenSpots}
                                    noOfParticipants={participants.filter(p => p.participating === true).length} />
                                {activities.filter(_ => _.id === activity.id).length > 0 && activity.options.length > 0 && participants.filter(p => p.participating === true).length > 0 && <div className='s4g-a-names s4g-a-names-margin-left'>
                                    {participants.filter(p => p.participating === true).map(p => {
                                        return (
                                            <React.Fragment key={p.id}>
                                                {activities.filter(_ => _.id === activity.id)[0].participants.length > 0 && <div className='s4g-a-name'>
                                                    <FormControl sx={{ width: '100%;' }}>
                                                        <InputLabel id="s4g-a-options-label">{p.name}'s val *</InputLabel>
                                                        <Select labelId="s4g-a-options-label" id="s4g-a-options-select" label={`${p.name}'s val *`} value={activities.filter(_ => _.id === activity.id)[0].participants.filter(_ => _.id === p.id)[0].option}
                                                            onChange={e => {
                                                                let updateActivities = [...activities];
                                                                let updateActivity = updateActivities.filter(a => a.id === activity.id)[0];
                                                                let updateActivityParticipant = { ...updateActivity.participants.filter(_ => _.id === p.id)[0] };

                                                                if (updateActivityParticipant) {
                                                                    let updateActivityIndex = updateActivities.findIndex(a => a.id === activity.id);
                                                                    updateActivities.splice(updateActivityIndex, 1);

                                                                    let updateActivityParticipantIndex = updateActivity.participants.findIndex(_ => _.id === p.id);
                                                                    updateActivity.participants.splice(updateActivityParticipantIndex, 1);

                                                                    updateActivityParticipant.option = e.target.value;
                                                                    updateActivity.participants.push(updateActivityParticipant);
                                                                    updateActivities.push(updateActivity);

                                                                    setActivities(updateActivities);
                                                                }
                                                            }}>
                                                            {activity.options.map(o => {
                                                                return (<MenuItem key={o} value={o}>{o}</MenuItem>)
                                                            })}
                                                        </Select>
                                                    </FormControl>
                                                </div>}
                                            </React.Fragment>
                                        )
                                    })}
                                </div>}
                            </React.Fragment>)
                        })}
                    </div>}

                    {member.players && <div>
                        <ValidationFeedback validationError={validationError} />
                        {reCAPTCHAActive === true && <div className='s4g-section'>
                            <ReCAPTCHA ref={recaptchaRef} hl="sv" sitekey="6LdbqJIeAAAAALRadjMPe9Ac5J3bJcHY073llqAl" onChange={e => { setApproveReCAPTCHA(true) }} />
                        </div>}
                        <div className='s4g-section'>
                            <LoadingButton
                                loading={submitting}
                                disabled={!approveReCAPTCHA && reCAPTCHAActive}
                                variant="contained"
                                loadingPosition="start"
                                startIcon={<SaveIcon />}
                                onClick={e => {
                                    validateRegistration();
                                }}>
                                {submitting ? "Skickar anmälan" : "Skicka in anmälan"}
                            </LoadingButton>
                        </div>
                    </div>}
                </React.Fragment>}
            </div>
        );
    }
}

export default ActivityForm;
